import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, PackageOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

export const DemoBadge = ({ label = "DEMO MODE" }) => (
  <span data-testid="demo-mode-badge" className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/40 bg-violet-500/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-300">
    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-400" />
    {label}
  </span>
);

const STATUS_COLORS = {
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  confirmed: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  processing: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  packed: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
  ready_to_ship: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
  shipped: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  out_for_delivery: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  delivered: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  refunded: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  returned: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  return_requested: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  rto: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  rto_in_transit: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  connected: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  live: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  cod_pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  cod_collected: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  draft: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  archived: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  out_of_stock: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  blocked: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  action_required: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  failed: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  open: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  requested: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  approved: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  pickup_scheduled: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
  in_transit: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  received: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  inspected: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  restocked: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  damaged: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  closed: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  verifying: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  not_connected: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

const STATUS_LABELS = {
  SELECTED: "Pending", ADMIN_REVIEW: "Processing", APPROVED: "Approved",
  PUBLISHING: "Publishing", LISTED: "Listed", REJECTED: "Rejected",
  RELEASED: "Released", REMOVED: "Removed", FAILED: "Failed",
};

const MP_STATUS_COLORS = {
  SELECTED: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  ADMIN_REVIEW: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  APPROVED: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  PUBLISHING: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
  LISTED: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  REJECTED: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  RELEASED: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  REMOVED: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  FAILED: "bg-rose-500/15 text-rose-400 border-rose-500/30",
};

export const StatusBadge = ({ status }) => (
  <span data-testid={`status-${status}`} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_COLORS[status] || MP_STATUS_COLORS[status] || "bg-slate-500/15 text-slate-400 border-slate-500/30"}`}>
    {STATUS_LABELS[status] || (status || "").replace(/_/g, " ")}
  </span>
);

export const StatCard = ({ icon: Icon, label, value, sub, accent = "text-orange-400", testid }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="rounded-xl border border-slate-800 bg-[#111827] p-4 shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-colors hover:border-slate-700"
  >
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
      {Icon && <Icon size={16} className={accent} />}
    </div>
    <p className="mt-2 text-2xl font-bold tracking-tight text-slate-50">{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </motion.div>
);

export const PageHeader = ({ title, sub, children, testid }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
    <div>
      <h1 data-testid={testid} className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">{title}</h1>
      {sub && <p className="mt-1 text-sm text-slate-400">{sub}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

export const Pager = ({ page, pages, onPage }) => (
  <div className="mt-4 flex items-center justify-between" data-testid="pager">
    <p className="text-xs text-slate-500">Page {page} of {pages}</p>
    <div className="flex gap-2">
      <Button data-testid="pager-prev" size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}
        className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800">
        <ChevronLeft size={14} /> Prev
      </Button>
      <Button data-testid="pager-next" size="sm" variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)}
        className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800">
        Next <ChevronRight size={14} />
      </Button>
    </div>
  </div>
);

export const EmptyState = ({ title = "Nothing here yet", sub }) => (
  <div data-testid="empty-state" className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-800 py-16 text-center">
    <PackageOpen className="mb-3 text-slate-600" size={32} />
    <p className="text-sm font-medium text-slate-300">{title}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);
